import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money, pct, moneyClass, dateShort } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import AddRepDialog from "@/components/AddRepDialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CaretUp, CaretDown, Users, PaperPlaneTilt, Broom } from "@phosphor-icons/react";

const COLS = [
  { key: "name", label: "Rep", align: "left" },
  { key: "status", label: "Status", align: "left" },
  { key: "revenue", label: "Revenue", align: "right" },
  { key: "deals_won", label: "Deals", align: "right" },
  { key: "close_rate", label: "Close %", align: "right" },
  { key: "commission_owed", label: "Owed", align: "right" },
  { key: "commission_paid", label: "Paid", align: "right" },
];

export default function Reps() {
  const [reps, setReps] = useState(null);
  const [sort, setSort] = useState({ key: "commission_owed", dir: "desc" });
  const [sweepOpen, setSweepOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/reps").then((r) => setReps(r.data));
  useEffect(() => { load(); }, []);

  const fireTestWebhook = async () => {
    try {
      const r = await api.post("/admin/test-webhook");
      r.data.signature_accepted
        ? toast.success(`Webhook wiring OK — HMAC verified, status "${r.data.webhook_status}"`)
        : toast.error(`Signature rejected (HTTP ${r.data.http_status})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sweepTestData = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup-test-reps");
      toast.success(`Removed ${r.data.removed_count} test rep(s)`);
      setSweepOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sorted = useMemo(() => {
    if (!reps) return [];
    const arr = reps.filter((r) => showDemo || !r.is_demo);
    arr.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = typeof av === "string" ? String(av).localeCompare(String(bv)) : (av - bv);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [reps, sort, showDemo]);

  const setSortKey = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Reps</h1>
          <p className="mt-1 text-sm text-muted-foreground">Leaderboard — sortable across revenue, close rate, and commission owed</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="mr-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Switch checked={showDemo} onCheckedChange={setShowDemo} data-testid="show-demo-toggle" /> Show demo rep
          </label>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="test-webhook-btn" onClick={fireTestWebhook}>
            <PaperPlaneTilt size={15} /> Send test webhook
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="cleanup-test-btn" onClick={() => setSweepOpen(true)}>
            <Broom size={15} /> Clean up test data
          </Button>
          <AddRepDialog onCreated={load} />
        </div>
      </div>

      <Dialog open={sweepOpen} onOpenChange={setSweepOpen}>
        <DialogContent data-testid="sweep-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Clean up test data?</DialogTitle>
            <DialogDescription>
              Permanently removes all reps named <span className="font-mono">TEST…</span> (and their deals,
              statements, contracts, W-9s and logins). The demo rep and real reps are never touched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSweepOpen(false)}>Cancel</Button>
            <Button data-testid="sweep-confirm-btn" disabled={busy} onClick={sweepTestData}>
              {busy ? "Cleaning…" : "Remove test reps"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {reps && reps.length === 0 ? (
        <div data-testid="reps-empty" className="rounded-sm border border-dashed border-border bg-card p-16 text-center">
          <Users size={40} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-heading text-lg font-semibold">No reps yet</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Onboard your first partner. Every rate is entered here per person — nothing is hard-coded.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-sm border border-border">
          <table className="w-full text-sm" data-testid="reps-table">
            <thead className="bg-card">
              <tr className="border-b border-border">
                {COLS.map((c) => (
                  <th key={c.key} onClick={() => setSortKey(c.key)}
                    className={`cursor-pointer select-none px-4 py-3 overline ${c.align === "right" ? "text-right" : "text-left"} hover:text-foreground`}>
                    <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                      {c.label}
                      {sort.key === c.key && (sort.dir === "asc" ? <CaretUp size={11} /> : <CaretDown size={11} />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} data-testid={`rep-row-${r.id}`} onClick={() => navigate(`/reps/${r.id}`)}
                  className="cursor-pointer border-b border-border bg-background transition-colors duration-200 hover:bg-accent">
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <StatusBadge status={r.status} />
                      {r.contract_status && r.contract_status !== "none" && <StatusBadge status={r.contract_status} />}
                      {r.stripe_payouts_enabled
                        ? <span className="inline-flex items-center rounded-sm border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-2 py-0.5 font-mono text-[11px] text-[hsl(var(--success))]">Stripe active</span>
                        : r.stripe_account_id
                          ? <span className="inline-flex items-center rounded-sm border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-2 py-0.5 font-mono text-[11px] text-[hsl(var(--warning))]">Stripe pending</span>
                          : <span className="inline-flex items-center rounded-sm border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">No Stripe</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{money(r.revenue)}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.deals_won}</td>
                  <td className="px-4 py-3 text-right font-mono">{pct(r.close_rate)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-medium ${moneyClass(r.commission_owed)}`}>{money(r.commission_owed)}</td>
                  <td className="px-4 py-3 text-right font-mono text-muted-foreground">{money(r.commission_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
